import { useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { useNavigate } from "react-router-dom";
import API from "../api/axiosConfig";

const SOCKET_URL = (
  process.env.REACT_APP_API_URL || "http://localhost:4000/api"
).replace(/\/api\/?$/, "");

function vibrate() {
  try { navigator.vibrate?.([200, 100, 200]); } catch (_) {}
}

function playDoorbell() {
  vibrate();
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playTone = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, start);
      gain.gain.setValueAtTime(0.4, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
      osc.start(start);
      osc.stop(start + dur);
    };
    // Resume primero (Chrome bloquea AudioContext sin gesto del usuario)
    ctx.resume().then(() => {
      playTone(659, ctx.currentTime, 0.6);
      playTone(494, ctx.currentTime + 0.65, 0.8);
    });
  } catch (_) {}
}

export default function RyderOrderNotifier() {
  const navigate = useNavigate();
  const [availableCount, setAvailableCount] = useState(0);
  const [ryderActive, setRyderActive] = useState(
    () => localStorage.getItem("ryderStatus") === "disponible"
  );
  const bellIntervalRef = useRef(null);

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  })();
  const isRyder = user?.role === "ryder";

  // ── Fetch pedidos disponibles ────────────────────────────────
  const fetchAvailable = useCallback(async () => {
    try {
      const res = await API.get("/orders/available");
      const list = res.data?.data?.data ?? res.data?.data ?? [];
      setAvailableCount(list.length);
    } catch (_) {}
  }, []);

  // ── Escuchar cambios de estado del ryder (mismo tab) ─────────
  useEffect(() => {
    if (!isRyder) return;

    const handler = (e) => {
      const newStatus = e.detail;
      setRyderActive(newStatus === "disponible");
      if (newStatus === "disponible") {
        fetchAvailable();
      } else {
        setAvailableCount(0);
        clearInterval(bellIntervalRef.current);
        bellIntervalRef.current = null;
      }
    };

    window.addEventListener("ryderStatusChange", handler);
    return () => window.removeEventListener("ryderStatusChange", handler);
  }, [isRyder, fetchAvailable]);

  // ── Carga inicial (si ya está disponible al montar) ───────────
  useEffect(() => {
    if (!isRyder) return;
    if (localStorage.getItem("ryderStatus") === "disponible") {
      fetchAvailable();
    }
  }, [isRyder, fetchAvailable]);

  // ── Timbre periódico mientras haya pedidos y esté activo ─────
  useEffect(() => {
    if (!isRyder || !ryderActive) return;

    if (availableCount > 0) {
      if (!bellIntervalRef.current) {
        playDoorbell();
        bellIntervalRef.current = setInterval(playDoorbell, 30000);
      }
    } else {
      clearInterval(bellIntervalRef.current);
      bellIntervalRef.current = null;
    }

    return () => {};
  }, [availableCount, ryderActive, isRyder]);

  // ── Socket.io global ─────────────────────────────────────────
  useEffect(() => {
    if (!isRyder) return;

    const token =
      localStorage.getItem("accessToken") || localStorage.getItem("token");
    if (!token) return;

    const socket = io(SOCKET_URL, {
      transports: ["websocket"],
      auth: { token },
    });

    socket.on("order:available", () => {
      if (localStorage.getItem("ryderStatus") !== "disponible") return;
      setAvailableCount((n) => n + 1);
      playDoorbell();
    });

    socket.on("order:assigned", () => {
      setAvailableCount((n) => Math.max(0, n - 1));
    });

    return () => {
      socket.disconnect();
      clearInterval(bellIntervalRef.current);
      bellIntervalRef.current = null;
    };
    // eslint-disable-next-line
  }, [isRyder]);

  if (!isRyder || !ryderActive || availableCount === 0) return null;

  return (
    <button
      onClick={() => navigate("/ryder-dashboard?tab=disponibles")}
      className="fixed bottom-20 right-4 z-[9999] flex items-center gap-2
        bg-amber-500 hover:bg-amber-400 text-black font-bold
        px-4 py-3 rounded-full shadow-lg shadow-amber-500/40
        animate-bounce transition"
      title="Ver pedidos disponibles"
    >
      🛵
      <span className="text-sm">
        {availableCount} pedido{availableCount > 1 ? "s" : ""} disponible{availableCount > 1 ? "s" : ""}
      </span>
      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-ping" />
    </button>
  );
}
